import { useEffect, useRef, useState } from "react";
import api, { formatError } from "@/lib/api";
import { useLocation } from "react-router-dom";
import { MessageCircle, X, Send, Sparkles } from "lucide-react";
import { chipsParaRuta } from "@/lib/ariChips";
import { getAriEstado, clearAriEstado } from "@/lib/ariEstado";

function nuevaSesion() {
  return "ari-" + Math.random().toString(36).slice(2) + Date.now().toString(36);
}

export default function AriWidget() {
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState([{ de: "ari", texto: "Hola 👋 Soy Ari, tu asistente de AuditReady. Pregúntame cómo hacer cualquier cosa en la plataforma." }]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [session] = useState(nuevaSesion);
  const scrollRef = useRef(null);
  const location = useLocation();
  const chips = chipsParaRuta(location.pathname);

  useEffect(() => { scrollRef.current?.scrollTo(0, scrollRef.current.scrollHeight); }, [msgs, open]);
  useEffect(() => { clearAriEstado(); }, [location.pathname]);

  const enviar = async (texto) => {
    const pregunta = (texto ?? input).trim();
    if (!pregunta || loading) return;
    setInput("");
    setMsgs((m) => [...m, { de: "user", texto: pregunta }]);
    setLoading(true);
    try {
      const est = getAriEstado();
      const contexto = (est.errores_visibles?.length || est.campos_vacios?.length) ? est : undefined;
      const { data } = await api.post("/asistente/preguntar", { pregunta, session_id: session, ruta: location.pathname, contexto });
      setMsgs((m) => [...m, { de: "ari", texto: data.respuesta, limite: data.limite }]);
    } catch (e) {
      setMsgs((m) => [...m, { de: "ari", texto: formatError(e.response?.data?.detail) || "No he podido responder ahora mismo." }]);
    } finally { setLoading(false); }
  };

  return (
    <>
      {!open && (
        <button onClick={() => setOpen(true)} data-testid="ari-abrir"
          className="fixed bottom-5 right-5 z-50 h-14 w-14 rounded-full bg-[#FF5625] hover:bg-[#E0451A] text-white shadow-xl flex items-center justify-center transition-transform hover:scale-105">
          <MessageCircle size={24} />
        </button>
      )}

      {open && (
        <div className="fixed bottom-5 right-5 z-50 w-[92vw] max-w-sm h-[70vh] max-h-[560px] bg-white rounded-xl shadow-2xl border border-[#E2E8F0] flex flex-col overflow-hidden" data-testid="ari-panel">
          <div className="bg-[#0E2A47] text-white px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Sparkles size={18} className="text-[#FF5625]" />
              <div className="leading-tight"><div className="font-bold text-sm">Ari · Asistente</div><div className="text-[10px] text-white/50">Ayuda de AuditReady</div></div>
            </div>
            <button onClick={() => setOpen(false)} data-testid="ari-cerrar" className="text-white/70 hover:text-white"><X size={18} /></button>
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto p-3 space-y-3" data-testid="ari-mensajes">
            {msgs.map((m, i) => (
              <div key={i} className={`flex ${m.de === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[85%] rounded-lg px-3 py-2 text-sm whitespace-pre-wrap ${m.de === "user" ? "bg-[#FF5625] text-white" : "bg-[#F1F5F9] text-[#0E2A47]"}`}>
                  {m.texto}
                </div>
              </div>
            ))}
            {loading && <div className="flex justify-start"><div className="bg-[#F1F5F9] text-[#0E2A47]/50 rounded-lg px-3 py-2 text-sm">Ari está escribiendo…</div></div>}
            {msgs.length <= 1 && (
              <div className="space-y-2 pt-1" data-testid="ari-chips">
                {chips.map((c, idx) => (
                  <button key={c} onClick={() => enviar(c)} className="block w-full text-left text-xs bg-white border border-[#E2E8F0] rounded-lg px-3 py-2 hover:border-[#FF5625] hover:text-[#FF5625] transition-colors" data-testid={`ari-chip-${idx}`}>{c}</button>
                ))}
              </div>
            )}
          </div>

          <div className="p-3 border-t border-[#E2E8F0] flex items-center gap-2">
            <input value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && enviar()}
              placeholder="Escribe tu pregunta…" data-testid="ari-input"
              className="flex-1 text-sm rounded-lg border border-[#E2E8F0] px-3 py-2 outline-none focus:border-[#FF5625]" />
            <button onClick={() => enviar()} disabled={loading} data-testid="ari-enviar"
              className="h-9 w-9 rounded-lg bg-[#0E2A47] hover:bg-[#163C65] text-white flex items-center justify-center shrink-0"><Send size={16} /></button>
          </div>
        </div>
      )}
    </>
  );
}
